import React, { FC, useEffect, useRef, useState } from 'react';
import { Box } from '@withjoy/joykit';
import { CroppedImageProps } from './CroppedImage.types';

export const CroppedImage: FC<CroppedImageProps> = props => {
  const { imageUrl, cropInfo, width, height, canvasStyles = {} } = props;

  const [hasError, setHasError] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const image = new Image();
    image.crossOrigin = 'anonymous'; // Allow cross-origin images
    image.src = imageUrl;

    image.onload = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          try {
            const { x, y, width: cropWidth, height: cropHeight } = cropInfo;

            // Convert percent values to pixels
            const cropX = (x / 100) * image.width;
            const cropY = (y / 100) * image.height;
            const cropWidthPx = (cropWidth / 100) * image.width;
            const cropHeightPx = (cropHeight / 100) * image.height;

            // Set canvas size to match the desired output size
            const devicePixelRatio = window.devicePixelRatio;
            canvas.width = width * devicePixelRatio;
            canvas.height = height * devicePixelRatio;
            ctx.scale(devicePixelRatio, devicePixelRatio);

            ctx.imageSmoothingQuality = 'high';

            // Clear canvas and draw the cropped image
            ctx.clearRect(0, 0, width, height);
            ctx.drawImage(
              image,
              cropX,
              cropY,
              cropWidthPx,
              cropHeightPx, // Source crop
              0,
              0,
              width,
              height // Destination canvas
            );
            setHasError(false);
          } catch (error) {
            setHasError(true);
            // Fallback to showing the full image
            canvas.width = width;
            canvas.height = height;
            ctx.clearRect(0, 0, width, height);
            ctx.drawImage(image, 0, 0, width, height);
          }
        }
      }
    };

    image.onerror = () => {
      setHasError(true);
    };
  }, [imageUrl, cropInfo, width, height]);

  // Fallback to background image if there's an error
  if (hasError) {
    return (
      <Box
        width={width}
        height={height}
        overflow="hidden"
        background={`url("${imageUrl}") lightgray 50%`}
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
      />
    );
  }

  return <Box as="canvas" ref={canvasRef} width={width} height={height} overflow="hidden" display="block" background="lightgray" {...canvasStyles} />;
};

CroppedImage.displayName = 'CroppedImage';
